import { useCallback, useSyncExternalStore } from 'react';

export const BREAKPOINTS = {
  xs: 0,
  sm: 600,
  md: 1000,
  lg: 1200,
  xl: 1920,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

// The tiers Tailwind mirrors, and so the only ones whose CSS and JS gates can be checked against
// each other. `xs` is the zero floor; `xl` keeps Tailwind's 1280 rather than this scale's 1920 —
// see the header comment in styles/theme.css.
export type AlignedBreakpoint = Extract<Breakpoint, 'sm' | 'md' | 'lg'>;
export const ALIGNED_BREAKPOINTS = ['sm', 'md', 'lg'] as const satisfies readonly AlignedBreakpoint[];

// `down`/`between` are exclusive of the upper bound by 0.05px, matching the semantics of MUI's
// `theme.breakpoints` these queries were migrated from — keeps the responsive cutoffs byte-identical.
export const media = {
  up: (bp: Breakpoint) => `(min-width:${BREAKPOINTS[bp]}px)`,
  down: (bp: Breakpoint) => `(max-width:${BREAKPOINTS[bp] - 0.05}px)`,
  between: (start: Breakpoint, end: Breakpoint) =>
    `(min-width:${BREAKPOINTS[start]}px) and (max-width:${BREAKPOINTS[end] - 0.05}px)`,
};

function hasMatchMedia(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!hasMatchMedia()) return () => undefined;
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    [query],
  );

  return useSyncExternalStore(
    subscribe,
    () => (hasMatchMedia() ? window.matchMedia(query).matches : false),
    () => false,
  );
}
